import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { subscribeLinking } from '@/lib/linkingState';

/**
 * Single source of truth for authentication readiness.
 * No role queries, instance queries, or redirects should run until isReady === true.
 * `isLinking` is true while useIdentityGuard runs claim_identity right after sign-in.
 *
 * Lifecycle: every effect run owns its own auth subscription (StrictMode's
 * cleanup + re-run resubscribes instead of leaving the hook deaf), and the
 * initial getSession() result is ignored once any auth event has arrived or
 * the run was disposed, so a late initial session can never overwrite a newer
 * signed-in/signed-out user.
 */
export function useAuthReady() {
  const [user, setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    let disposed = false;
    let sawAuthEvent = false;

    // Subscribe to auth changes FIRST (before getSession)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (disposed) return;
      sawAuthEvent = true;
      setUser(session?.user ?? null);
      setIsReady(true);
    });

    // Then restore session — only if nothing newer has been observed.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (disposed || sawAuthEvent) return;
      setUser(session?.user ?? null);
      setIsReady(true);
    });

    const unsubLinking = subscribeLinking((l) => { if (!disposed) setIsLinking(l); });

    return () => {
      disposed = true;
      subscription.unsubscribe();
      unsubLinking();
    };
  }, []);

  return { user, isReady, isLinking };
}
